import type { PlanBeat, PlanEffectEnvelope } from '@toolproof-core/lib/_contract/scene-plan';

import { planAnchorIndex } from './episode';
import type { EpisodeCameraViewResolver, EpisodeTraceCameraPolicy } from '@/_contract/episodePresentation';
import type { TraceReplay } from '@/_contract/trace';

export function parseEpisodeTraceCameraPolicy<TEffect extends PlanEffectEnvelope>(
    value: unknown, beats: readonly PlanBeat<TEffect>[],
): EpisodeTraceCameraPolicy | undefined {
    if (value === undefined) return undefined;
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid episode camera policy.');
    const policy = value as Record<string, unknown>;
    for (const key of Object.keys(policy)) {
        if (key !== 'completion' && key !== 'arrivalViews') throw new Error(`Unknown episode camera policy field '${key}'.`);
    }
    if (policy.completion !== 'overview' && policy.completion !== 'choreography') {
        throw new Error('Episode camera completion must be overview or choreography.');
    }
    const arrivalViews = policy.arrivalViews;
    if (arrivalViews !== undefined && (!Array.isArray(arrivalViews)
        || !arrivalViews.every(view => typeof view === 'string' && view.trim())
        || new Set(arrivalViews).size !== arrivalViews.length)) {
        throw new Error('Episode arrivalViews must contain unique nonempty camera views.');
    }
    for (const view of arrivalViews ?? []) {
        if (!beats.some(beat => beat.presentation?.some(segment => segment.cameraView === view
            && beat.cues?.some(cue => cue.id === segment.afterCue && cue.phase !== 'start')))) {
            throw new Error(`Episode arrival view '${view}' requires a completed-cue camera presentation.`);
        }
    }
    return { completion: policy.completion, ...(arrivalViews === undefined ? {} : { arrivalViews }) };
}

export function resolveTraceEpisodeCameraPolicy<TEffect extends PlanEffectEnvelope>(
    scene: { beats: readonly PlanBeat<TEffect>[]; cameraPolicy?: EpisodeTraceCameraPolicy },
): EpisodeTraceCameraPolicy {
    return scene.cameraPolicy ?? {
        completion: scene.beats.some(beat => beat.presentation?.some(segment => segment.cameraView !== undefined))
            ? 'choreography' : 'overview',
    };
}

export function selectEpisodeArrivalCameraView<TEffect extends PlanEffectEnvelope>(args: {
    beat: PlanBeat<TEffect> | undefined; replay: TraceReplay; pendingStepIndex: number;
    policy: EpisodeTraceCameraPolicy;
}): string | undefined {
    return args.beat?.presentation?.find(segment => {
        const cue = args.beat?.cues?.find(entry => entry.id === segment.afterCue);
        return segment.cameraView !== undefined && args.policy.arrivalViews?.includes(segment.cameraView)
            && cue && cue.phase !== 'start'
            && planAnchorIndex(args.replay, cue) === args.pendingStepIndex;
    })?.cameraView;
}

export function selectEpisodeCompletionCameraView(args: {
    atCompletion: boolean; policy: EpisodeTraceCameraPolicy | undefined;
    authoredView?: string; arrivalView?: string;
}): 'overview' | undefined {
    return args.atCompletion && args.policy?.completion !== 'choreography'
        && args.authoredView === undefined && args.arrivalView === undefined ? 'overview' : undefined;
}

export function resolveEpisodeCameraView(
    cameraView: string | undefined, resolver: EpisodeCameraViewResolver,
): string | undefined {
    if (cameraView === undefined) return undefined;
    const view = resolver.viewId(cameraView);
    if (!resolver.hasView(view)) throw new Error(`Episode camera view '${cameraView}' is unavailable.`);
    return view;
}
